import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { useAccess, canVisit, type AccessAction } from './access';

export const Permission = ({
  resource,
  action = 'read',
  children,
}: {
  resource: string;
  action?: AccessAction;
  children: ReactNode;
}) => {
  const access = useAccess();
  return access.can(resource, action) ? children : null;
};

export const AccessBoundary = ({ children }: { children: ReactNode }) => {
  const access = useAccess();
  const cache = useQueryClient();
  const previous = useRef<string | null>(null);
  const { pathname, search } = useLocation();
  const signature = access.data ? JSON.stringify(access.data) : null;
  useEffect(() => {
    if (signature && previous.current && previous.current !== signature) {
      // Remove cached records when visibility changes, including inactive detail pages.
      void cache
        .cancelQueries({ predicate: (query) => query.queryKey[0] !== 'access-me' })
        .then(() => {
          cache
            .resetQueries({ predicate: (query) => query.queryKey[0] !== 'access-me' })
            .catch(() => undefined);
        });
    }
    previous.current = signature;
  }, [signature, cache]);
  if (access.isPending)
    return (
      <p role="status" className="p-6 text-sm text-navy-500">
        Vérification des permissions…
      </p>
    );
  if (access.isError)
    return (
      <div role="alert" className="p-6">
        <p>Impossible de vérifier vos permissions.</p>
        <Button onClick={() => void access.refetch()}>Réessayer</Button>
      </div>
    );
  if (!canVisit(access.data, pathname + search))
    return (
      <div className="rounded-xl border border-navy-100 bg-white p-8">
        <h1 className="text-xl font-semibold text-navy-900">Accès non autorisé</h1>
        <p className="mt-2 text-sm text-ink-500">
          Votre rôle ne donne pas accès à cette page. Contactez votre administrateur pour modifier
          vos permissions.
        </p>
      </div>
    );
  return children;
};
